// assets/knife-engraving.js
import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Alles außer Buchstaben (inkl. Umlaute/Akzente), Ziffern, Leerzeichen und den Standard-Sonderzeichen,
 * die Cinzel und Cinzel Decorative beide enthalten: ! " # $ % & ' ( ) * + , - . / : ; < = > ? @ [ \ ] ^ _ { | } ~ ’
 */
const DISALLOWED_CHARACTERS = /[^\p{L}\p{M}\p{N} !"#$%&'()*+,\-./:;<=>?@[\\\]^_{|}~’]/gu;

/** Anteil der Gravurfeld-Höhe als Schriftgröße, falls kein Maßstab gesetzt ist. */
const TEXT_HEIGHT_RATIO = 0.8;

/** 1 typografischer Punkt in Millimetern. */
const MM_PER_PT = 25.4 / 72;

/**
 * @typedef {{ id: number | string, title: string, options?: string[] }} Variant
 * @typedef {{ element: HTMLElement, title: string, property: string, text: string }} ActiveSide
 */

/**
 * Gravur-Konfigurator (blocks/knife-engraving.liquid).
 *
 * Hängt sich über das `formdata`-Event an das Produktformular von Horizon: Sobald das Theme die
 * FormData für "In den Warenkorb" baut, ergänzen wir die Gravur-Eigenschaften am Messer und das
 * Gravurprodukt als zusätzliche Position. So bleibt product-form.js unverändert.
 *
 * @extends Component
 */
class KnifeEngravingComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {Variant | null} */
  #variant = null;

  /** @type {MutationObserver | null} */
  #lockObserver = null;

  /** @type {number | null} */
  #lockFrame = null;

  /** @type {HTMLButtonElement | null} */
  #lockHint = null;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#variant = this.#readInitialVariant();

    this.addEventListener('input', this.#onInput, { signal });
    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('click', this.#onClick, { signal });
    // Das Formular liegt außerhalb dieses Blocks – daher auf document im Capture-Modus lauschen.
    document.addEventListener('formdata', this.#onFormData, { capture: true, signal });

    const scope = this.closest('.shopify-section') ?? document;
    scope.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });

    this.#resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const side = /** @type {HTMLElement} */ (entry.target).closest('[data-ke-side]');
        if (side instanceof HTMLElement) this.#fitText(side);
      }
    });

    document.fonts?.ready.then(() => this.#fitAll());

    // Horizon setzt den Warenkorb-Button bei Variantenwechsel/Fehlern neu auf – Sperre danach erneut prüfen.
    this.#lockObserver = new MutationObserver(() => this.#scheduleLockSync());
    this.#lockObserver.observe(scope === document ? document.body : scope, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['disabled'],
    });

    this.#setup();
    if (this.dataset.editorOpen === 'true') this.#openAllForEditor();
    this.#update();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#setup();
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#resizeObserver?.disconnect();
    this.#lockObserver?.disconnect();
    if (this.#lockFrame) cancelAnimationFrame(this.#lockFrame);
    this.#releaseLock();
    this.#lockHint?.remove();
    this.#lockHint = null;
  }

  // ---------------------------------------------------------------------------
  // Getter
  // ---------------------------------------------------------------------------

  get #enabled() {
    return this.querySelector('[data-ke-enable]')?.checked === true;
  }

  /** @returns {HTMLElement[]} */
  get #sides() {
    return Array.from(this.querySelectorAll('[data-ke-side]'));
  }

  get #maxLength() {
    return Number(this.dataset.maxLength) || 25;
  }

  /** @returns {HTMLInputElement | null} */
  get #selectedFont() {
    return this.querySelector('[data-ke-font]:checked') ?? this.querySelector('[data-ke-font]');
  }

  /** @param {HTMLElement} side */
  #isSideActive(side) {
    if (!this.#enabled) return false;
    // Nur eine Gravurseite: die Haupt-Checkbox reicht, die Seitenauswahl ist ausgeblendet.
    if (this.#isSingleSide) return true;
    return side.querySelector('[data-ke-side-toggle]')?.checked === true;
  }

  get #isSingleSide() {
    return this.#sides.length === 1;
  }

  /** @returns {ActiveSide[]} */
  #getActiveSides() {
    return this.#sides
      .filter((side) => this.#isSideActive(side))
      .map((side) => ({
        element: side,
        title: side.dataset.title ?? '',
        property: side.dataset.property ?? side.dataset.title ?? '',
        text: (side.querySelector('[data-ke-input]')?.value ?? '').trim(),
      }));
  }

  // ---------------------------------------------------------------------------
  // Setup & Rendering
  // ---------------------------------------------------------------------------

  #setup() {
    const priceLabel = this.#formatMoney(Number(this.dataset.priceCents) || 0);

    for (const side of this.#sides) {
      const input = side.querySelector('[data-ke-input]');
      if (input instanceof HTMLInputElement) {
        input.maxLength = this.#maxLength;
        input.setAttribute('form', this.dataset.formId ?? '');
      }

      const price = side.querySelector('[data-ke-price]');
      if (price) price.textContent = priceLabel;

      const zone = side.querySelector('[data-ke-zone]');
      if (zone) this.#resizeObserver?.observe(zone);
    }

    this.#updateImages();
  }

  #openAllForEditor() {
    const enable = this.querySelector('[data-ke-enable]');
    if (enable instanceof HTMLInputElement) enable.checked = true;
    for (const toggle of this.querySelectorAll('[data-ke-side-toggle]')) {
      if (toggle instanceof HTMLInputElement) toggle.checked = true;
    }
  }

  /** Synchronisiert Sichtbarkeit, Pflichtfelder, Vorschau und Gesamtpreis mit dem aktuellen Zustand. */
  #update() {
    const enabled = this.#enabled;
    const panel = this.querySelector('[data-ke-panel]');
    if (panel instanceof HTMLElement) panel.hidden = !enabled;

    const font = this.#selectedFont;
    if (font?.dataset.family) this.style.setProperty('--ke-font-family', font.dataset.family);

    let activeCount = 0;
    for (const side of this.#sides) {
      const active = this.#isSideActive(side);
      if (active) activeCount += 1;

      const body = side.querySelector('[data-ke-side-body]');
      if (body instanceof HTMLElement) body.hidden = !active;

      const input = side.querySelector('[data-ke-input]');
      if (input instanceof HTMLInputElement) {
        input.disabled = !active;
        input.required = active;
        this.#validate(input, active);
      }

      this.#renderPreview(side);
    }

    const consent = this.querySelector('[data-ke-consent]');
    if (consent instanceof HTMLInputElement) {
      const needed = enabled && activeCount > 0;
      consent.disabled = !needed;
      consent.required = needed;
    }

    const summary = this.querySelector('[data-ke-summary]');
    const total = this.querySelector('[data-ke-total]');
    // Gesamtpreis nur bei mehreren Seiten – bei einer Seite steht der Preis schon an der Checkbox.
    if (summary instanceof HTMLElement) summary.hidden = activeCount === 0 || this.#isSingleSide;
    if (total) total.textContent = this.#formatMoney(activeCount * (Number(this.dataset.priceCents) || 0));

    this.#syncLock();
  }

  // ---------------------------------------------------------------------------
  // Warenkorb-Button sperren, solange die Gravur unvollständig ist
  // ---------------------------------------------------------------------------
  //
  // Apps wie "Sticky Cart by Qikify" fangen den Klick auf "In den Warenkorb" ab und umgehen damit
  // die Pflichtfeld-Prüfung des Browsers. Ein deaktivierter Button löst gar keinen Klick aus –
  // das hält jede App auf.

  /** Gravur aus oder vollständig ausgefüllt (Text + Pflicht-Checkbox). */
  get #isComplete() {
    if (!this.#enabled) return true;
    const fields = /** @type {HTMLInputElement[]} */ (
      Array.from(this.querySelectorAll('[data-ke-input], [data-ke-consent]'))
    );
    return fields.filter((field) => !field.disabled).every((field) => field.validity.valid);
  }

  /** @returns {HTMLButtonElement[]} Hauptbutton und Sticky-Button von Horizon */
  #getCartButtons() {
    const form = document.getElementById(this.dataset.formId ?? '');
    const section = this.closest('.shopify-section');
    const buttons = [
      ...(form?.querySelectorAll('button[type="submit"]') ?? []),
      ...(section?.querySelectorAll('sticky-add-to-cart [ref="addToCartButton"]') ?? []),
    ];
    return /** @type {HTMLButtonElement[]} */ (buttons.filter((button) => button instanceof HTMLButtonElement));
  }

  #scheduleLockSync() {
    if (this.#lockFrame) return;
    this.#lockFrame = requestAnimationFrame(() => {
      this.#lockFrame = null;
      this.#syncLock();
    });
  }

  #syncLock() {
    const locked = !this.#isComplete;

    if (locked) {
      for (const button of this.#getCartButtons()) {
        if (button.disabled) continue;
        button.disabled = true;
        button.dataset.keLocked = 'true';
      }
    } else {
      this.#releaseLock();
    }

    this.#renderLockHint(locked);
  }

  /** Gibt nur Buttons frei, die wir gesperrt haben – und nur, wenn die Variante verfügbar ist. */
  #releaseLock() {
    const available = /** @type {any} */ (this.#variant)?.available !== false;
    for (const button of this.#getCartButtons()) {
      if (button.dataset.keLocked !== 'true') continue;
      delete button.dataset.keLocked;
      if (available) button.disabled = false;
    }
  }

  /** @param {boolean} visible */
  #renderLockHint(visible) {
    const form = document.getElementById(this.dataset.formId ?? '');
    const anchor = form?.querySelector('add-to-cart-component') ?? form?.querySelector('button[type="submit"]');

    if (!visible || !anchor) {
      if (this.#lockHint) this.#lockHint.hidden = true;
      return;
    }

    if (!this.#lockHint) {
      const hint = document.createElement('button');
      hint.type = 'button';
      hint.className = 'ke-lock-hint';
      hint.setAttribute('aria-live', 'polite');
      hint.textContent = this.dataset.lockHint || 'Bitte Gravurtext eingeben und den Hinweis bestätigen.';
      hint.addEventListener('click', () => this.#revealMissingField());
      this.#lockHint = hint;
    }

    // Horizon kann den Bereich neu rendern – Hinweis bei Bedarf wieder einsetzen.
    if (this.#lockHint.previousElementSibling !== anchor) anchor.insertAdjacentElement('afterend', this.#lockHint);
    this.#lockHint.hidden = false;
  }

  /** Springt zum ersten fehlenden Feld und zeigt dort die Fehlermeldung. */
  #revealMissingField() {
    const fields = /** @type {HTMLInputElement[]} */ (
      Array.from(this.querySelectorAll('[data-ke-input], [data-ke-consent]'))
    );
    const missing = fields.find((field) => !field.disabled && !field.validity.valid);
    if (!missing) return;
    missing.scrollIntoView({ block: 'center', behavior: 'smooth' });
    missing.focus({ preventScroll: true });
    missing.reportValidity();
  }

  /** @param {HTMLElement} side */
  #renderPreview(side) {
    const preview = side.querySelector('[data-ke-preview]');
    const input = side.querySelector('[data-ke-input]');
    const count = side.querySelector('[data-ke-count]');
    if (!(preview instanceof HTMLElement) || !(input instanceof HTMLInputElement)) return;

    const text = input.value.trim();
    let sample = this.dataset.sampleText ?? '';
    if (this.dataset.uppercase === 'true') sample = sample.toLocaleUpperCase('de-DE');

    preview.textContent = text || sample;
    preview.toggleAttribute('data-sample', !text);

    if (count) count.textContent = `${input.value.length} / ${this.#maxLength}`;

    this.#fitText(side);
  }

  /**
   * Setzt die Vorschau-Schriftgröße maßstabsgetreu (pt → mm → px anhand der Bildbreite)
   * und verkleinert nur, falls der Text sonst über das Gravurfeld hinausragt.
   * @param {HTMLElement} side
   */
  #fitText(side) {
    const zone = side.querySelector('[data-ke-zone]');
    const preview = side.querySelector('[data-ke-preview]');
    const box = side.querySelector('[data-ke-preview-box]');
    if (!(zone instanceof HTMLElement) || !(preview instanceof HTMLElement)) return;

    const { clientWidth: width, clientHeight: height } = zone;
    if (!width || !height) return;

    const fontPt = Number(side.dataset.fontPt) || 0;
    const imageMm = Number(side.dataset.imageMm) || 0;
    const boxWidth = box instanceof HTMLElement ? box.clientWidth : 0;

    const baseSize =
      fontPt && imageMm && boxWidth ? fontPt * MM_PER_PT * (boxWidth / imageMm) : height * TEXT_HEIGHT_RATIO;
    preview.style.fontSize = `${baseSize}px`;

    const textWidth = preview.scrollWidth;
    if (textWidth > width) {
      preview.style.fontSize = `${Math.max(4, baseSize * (width / textWidth) * 0.98)}px`;
    }
  }

  #fitAll() {
    for (const side of this.#sides) this.#fitText(side);
  }

  /** Zeigt pro Seite das Bild der aktuell gewählten Variante. */
  #updateImages() {
    const options = (this.#variant?.options ?? []).map((value) => String(value).trim().toLowerCase());

    for (const side of this.#sides) {
      /** @type {HTMLElement | null} */
      let match = null;

      for (const image of side.querySelectorAll('[data-ke-image]')) {
        if (!(image instanceof HTMLElement)) continue;
        const isMatch = !match && options.includes(image.dataset.optionValue ?? '');
        image.toggleAttribute('data-active', isMatch);
        if (isMatch) match = image;
      }

      const color = match?.dataset.textColor;
      if (color) {
        side.style.setProperty('--ke-text-color', color);
      } else {
        side.style.removeProperty('--ke-text-color');
      }

      this.#fitText(side);
    }
  }

  // ---------------------------------------------------------------------------
  // Eingabe & Validierung
  // ---------------------------------------------------------------------------

  /** @param {Event} event */
  #onInput = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches('[data-ke-input]')) return;

    let value = input.value.replace(DISALLOWED_CHARACTERS, '');
    if (this.dataset.uppercase === 'true') value = value.toLocaleUpperCase('de-DE');
    value = value.slice(0, this.#maxLength);

    if (value !== input.value) {
      const caret = Math.min(input.selectionStart ?? value.length, value.length);
      input.value = value;
      input.setSelectionRange(caret, caret);
    }

    this.#validate(input, true);
    const side = input.closest('[data-ke-side]');
    if (side instanceof HTMLElement) this.#renderPreview(side);
    this.#syncLock();
  };

  /** @param {Event} event */
  #onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    if (target.matches('[data-ke-font]')) {
      const family = target.dataset.family ?? '';
      this.style.setProperty('--ke-font-family', family);
      document.fonts?.load(`20px ${family}`).finally(() => this.#fitAll());
    }

    if (target.matches('[data-ke-consent]')) {
      this.#syncLock();
    }

    if (target.matches('[data-ke-enable], [data-ke-side-toggle], [data-ke-font]')) {
      this.#update();

      // Beim Aufklappen einer Seite direkt ins Textfeld springen
      if (target.checked && target.matches('[data-ke-side-toggle]')) {
        target.closest('[data-ke-side]')?.querySelector('[data-ke-input]')?.focus({ preventScroll: true });
      }
      if (target.checked && target.matches('[data-ke-enable]') && this.#isSingleSide) {
        this.querySelector('[data-ke-input]')?.focus({ preventScroll: true });
      }
    }
  };

  /**
   * Lupe: Klick auf den Lupen-Button oder das Vorschaubild vergrößert das Gravurfeld (und zurück).
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const trigger = target.closest('[data-ke-zoom], [data-ke-preview-box]');
    const side = trigger?.closest('[data-ke-side]');
    if (!(side instanceof HTMLElement) || !this.contains(side)) return;

    const zoomed = !side.hasAttribute('data-zoomed');
    side.toggleAttribute('data-zoomed', zoomed);

    const button = side.querySelector('[data-ke-zoom]');
    if (button instanceof HTMLElement) {
      button.setAttribute('aria-pressed', String(zoomed));
      const label = zoomed ? button.dataset.labelOut : button.dataset.labelIn;
      if (label) button.setAttribute('aria-label', label);
    }
  };

  /**
   * @param {HTMLInputElement} input
   * @param {boolean} active
   */
  #validate(input, active) {
    const isEmpty = input.value.trim() === '';
    input.setCustomValidity(active && isEmpty ? this.dataset.emptyError || 'Bitte Gravurtext eingeben.' : '');
  }

  // ---------------------------------------------------------------------------
  // Variante
  // ---------------------------------------------------------------------------

  /** @returns {Variant | null} */
  #readInitialVariant() {
    const script = this.querySelector('script[data-ke-variant]');
    try {
      return script?.textContent ? JSON.parse(script.textContent) : null;
    } catch {
      return null;
    }
  }

  /** @param {Event & { promise?: Promise<any> }} event */
  #onProductSelect = async (event) => {
    try {
      const result = await (event.promise ?? Promise.resolve(event));
      const detail = result?.detail;
      const resource = detail?.resource;
      if (!resource) return;

      const productId = detail.newProduct?.id ?? detail.productId;
      if (productId && String(productId) !== this.dataset.productId) return;

      this.#variant = resource;
      this.#updateImages();
      this.#syncLock();
    } catch (error) {
      if (/** @type {Error} */ (error)?.name !== 'AbortError') console.warn('[knife-engraving]', error);
    }
  };

  // ---------------------------------------------------------------------------
  // Warenkorb
  // ---------------------------------------------------------------------------

  /** @param {FormDataEvent} event */
  #onFormData = (event) => {
    const form = event.target;
    // getAttribute statt form.id: das Feld name="id" (Varianten-ID) überschattet form.id
    if (!(form instanceof HTMLFormElement) || form.getAttribute('id') !== this.dataset.formId) return;

    const sides = this.#getActiveSides().filter((side) => side.text);
    if (sides.length === 0) return;

    const { formData } = event;
    const knifeVariantId = String(formData.get('id') ?? '');
    if (!knifeVariantId) return;

    const engravingId = this.#createId();
    const fontName = this.#selectedFont?.value ?? '';
    const fontProperty = this.dataset.fontProperty || 'Schriftart';
    const knifeQuantity = Number(formData.get('quantity')) || 1;

    // Messer: Gravur-Eigenschaften (gleiches Format wie bisher mit Customily)
    for (const side of sides) {
      formData.append(`properties[${side.property}]`, side.text);
    }
    if (fontName) formData.append(`properties[${fontProperty}]`, fontName);
    formData.append('properties[_gravur_id]', engravingId);
    formData.append('properties[_gravur_hinweis_bestaetigt]', 'ja');

    // Gravurprodukt: eine Einheit pro Seite und Messer
    formData.append('items[0][id]', this.dataset.engravingVariantId ?? '');
    formData.append('items[0][quantity]', String(sides.length * knifeQuantity));
    if (this.dataset.isBundle !== 'true') {
      formData.append('items[0][parent_id]', knifeVariantId);
    }
    formData.append('items[0][properties][Messer]', this.#knifeLabel(knifeVariantId));
    for (const side of sides) {
      formData.append(`items[0][properties][${side.title}]`, side.text);
    }
    if (fontName) formData.append(`items[0][properties][${fontProperty}]`, fontName);
    formData.append('items[0][properties][_gravur_ref]', engravingId);
  };

  /** @param {string} variantId */
  #knifeLabel(variantId) {
    const title = this.dataset.productTitle ?? '';
    const variant = this.#variant;
    if (!variant || String(variant.id) !== variantId || !variant.title || variant.title === 'Default Title') {
      return title;
    }
    return `${title} – ${variant.title}`;
  }

  #createId() {
    const random = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}${Math.random()}`;
    return random.replace(/[^a-z0-9]/gi, '').slice(0, 10);
  }

  /** @param {number} cents */
  #formatMoney(cents) {
    try {
      return new Intl.NumberFormat(this.dataset.locale || 'de-AT', {
        style: 'currency',
        currency: this.dataset.currency || 'EUR',
      }).format(cents / 100);
    } catch {
      return `${(cents / 100).toFixed(2).replace('.', ',')} €`;
    }
  }
}

if (!customElements.get('knife-engraving-component')) {
  customElements.define('knife-engraving-component', KnifeEngravingComponent);
}
